import { Vector2, Vector3 } from "three";
import { EMaterialSlot } from "../../Meshes/EMaterialSlot";
import { SubMesh } from "../../Meshes/SubMesh";
import { SubMeshCollection } from "../../Meshes/SubMeshCollection";
import { VerticeHelper } from "../../Meshes/VerticeHelper";
import { FrontElementMetadata } from "../Metadata/FrontMetadata";
import { BaseFrontElementGenerator } from "./BaseFrontElementGenerator";
import { Padding } from "../../Meshes/Padding";
import LengthValue from "$src/CGS/domain/dto/core/Length/LengthValue";
import { ICabinetShapeGeneratorParameters } from "../ShapeGenerators/ICabinetShapeGeneratorParameters";

enum EUVCartography {
    None,   // de 0 à 10cm
    Drawer, // de 10 à 20cm
    Door    // plus de 20cm
};

export class FixedPanelGenerator extends BaseFrontElementGenerator {
   
    constructor(frontElementMetadata: FrontElementMetadata, doorThickness: LengthValue, parameters: ICabinetShapeGeneratorParameters) {
        super(frontElementMetadata, doorThickness);
    }

    private get Cartography(): EUVCartography {
        // Calcul des UV à utiliser
        if (this.size.y >= 0.25 && this.size.x >= 0.25) return EUVCartography.Door;
        if (this.size.y >= 0.1 && this.size.x >= 0.1) return EUVCartography.Drawer;

        return EUVCartography.None;
    }

    private get CartographyPadding(): number {
        switch(this.Cartography) {
            case EUVCartography.Drawer: return 0.04;   // 4cm
            case EUVCartography.Door: return 0.12;     // 12cm
            default: return 0;
        }
    }

    private get CartographyUV(): Vector2[] {
        switch (this.Cartography) {
            case EUVCartography.Door:
                return [
                    new Vector2(0.7, 0.3),
                    new Vector2(0.8499, 0.3),
                    new Vector2(0.8501, 0.3),
                    new Vector2(1,   0.3),

                    new Vector2(0.7, 0.1501),
                    new Vector2(0.8499, 0.1501),
                    new Vector2(0.8501, 0.1501),
                    new Vector2(1,   0.1501),

                    new Vector2(0.7, 0.1499),
                    new Vector2(0.8499, 0.1499),
                    new Vector2(0.8501, 0.1499),
                    new Vector2(1,   0.1499),

                    new Vector2(0.7, 0),
                    new Vector2(0.8499, 0),
                    new Vector2(0.8501, 0),
                    new Vector2(1,   0)
                ];

            case EUVCartography.Drawer:
                return [
                    new Vector2(0.9, 1),
                    new Vector2(0.9499, 1),
                    new Vector2(0.9501, 1),
                    new Vector2(1,   1),

                    new Vector2(0.9, 0.9501),
                    new Vector2(0.9499, 0.9501),
                    new Vector2(0.9501, 0.9501),
                    new Vector2(1,   0.9501),

                    new Vector2(0.9, 0.9499),
                    new Vector2(0.9499, 0.9499),
                    new Vector2(0.9501, 0.9499),
                    new Vector2(1,   0.9499),

                    new Vector2(0.9, 0.9),
                    new Vector2(0.9499, 0.9),
                    new Vector2(0.9501, 0.9),
                    new Vector2(1,   0.9)
                ];

            default: return Array(16).fill(new Vector2(0, 0));
        }
    }

    protected override buildFront(): SubMeshCollection {
        let cornerPositions = this.cornerPositions.slice(4);
        let vertices: Vector3[] = VerticeHelper.split9Quad(cornerPositions[0], cornerPositions[1], cornerPositions[2], cornerPositions[3], new Padding(this.CartographyPadding));

        let indices: number[] = [
            0, 1, 5, 5, 4, 0,       // HAUT GAUCHE
            1, 2, 6, 6, 5, 1,       // HAUT CENTRE
            2, 3, 7, 7, 6, 2,       // HAUT DROITE

            8, 9, 13, 13, 12, 8,    // BAS GAUCHE
            9, 10, 14, 14, 13, 9,   // BAS CENTRE
            10, 11, 15, 15, 14, 10, // BAS DROITE

            4, 5, 9, 9, 8, 4,       // MILIEU GAUCHE
            5, 6, 10, 10, 9, 5,     // MILIEU CENTRE
            6, 7, 11, 11, 10, 6     // MILIEU DROIT
        ];

        let uvs: Vector2[] = this.CartographyUV;

        let collection = new SubMeshCollection();
        collection.add(new SubMesh(vertices, indices, uvs), EMaterialSlot.Door);
        return collection;
    }
}
